import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DragDropProvider, DragEndEvent } from '@dnd-kit/react';
import { move } from '@dnd-kit/helpers';

import { Task, TaskLevel } from '../../../../app/entities';
import { catchError } from '../../../../core/catch-error';
import { useCurrentProject } from '../../../../hooks/use-current-project';
import { useCurrentSpace } from '../../../../hooks/use-current-space';
import { useCurrentMember } from '../../../../hooks/use-current-member';
import { authStore } from '../../../../core/stores/auth-store';
import { openModal } from '../../../../core/modal/open-modal';
import { CreateTaskModal } from '../create-task.modal';
import { getActiveGroupBy } from '../../task-grouping-options';
import { TaskColumn } from './task-column';
import { TaskCard } from './task-card';
import {
  bucketTasksIntoColumns,
  getColumnMovePatch,
  getTaskColumnIds,
  getTaskColumns,
  TaskColumn as TaskColumnDescriptor,
} from './task-board.utils';

import styles from './task-board.module.scss';
type TaskBoardProps = {
  tasks: Task[];
  taskLevel: TaskLevel;

  /**
   * Override the default navigation when clicking on a card
   */
  onSelectTask?: (taskId: string) => void;
};

/**
 * Kanban board for the tasks feature. One column per group for the active
 * grouping (including empty columns). Dragging a card to another column
 * persists the grouped field via `task.update`. Drag is disabled when grouping
 * by tag (a task can belong to several tag columns, so a move is ambiguous).
 */
export const TaskBoard: React.FC<TaskBoardProps> = observer(
  ({ tasks, taskLevel, onSelectTask }) => {
    const project = useCurrentProject();
    const space = useCurrentSpace();
    const currentMember = useCurrentMember();
    const activeGroupBy = getActiveGroupBy(taskLevel);
    const isDragDisabled = activeGroupBy.key === 'tag';
    const isViewOnly = !currentMember || currentMember.role === 'guest';

    // Reading the observable project/space relations here (under `observer`)
    // keeps the column set reactive to membership / step / project changes.
    const columns = getTaskColumns(activeGroupBy, taskLevel, { project, space });
    const columnsKey = columns.map((column) => column.id).join('|');

    const [items, setItems] = useState<Record<string, string[]>>(() =>
      bucketTasksIntoColumns(columns, tasks, activeGroupBy.key),
    );

    // `columnsKey` captures column identity/order; recompute when tasks,
    // grouping or the column set change (intentionally not depending on the
    // `columns` array identity, which changes every render).
    useEffect(() => {
      setItems(bucketTasksIntoColumns(columns, tasks, activeGroupBy.key));
    }, [tasks, activeGroupBy.key, columnsKey]);

    const handleDragEnd: DragEndEvent = (event) => {
      const { source } = event.operation;

      if (event.canceled || !source || source.type !== 'item') {
        return;
      }

      const task = Task.getOne(source.id as string) as Task;
      if (!task) return;

      const originalColumnId = getTaskColumnIds(task, activeGroupBy.key)[0];
      // `group` is a sortable-only field not surfaced on the base Draggable type.
      const targetColumnId = (source as { group?: string }).group;

      // No cross-column move → nothing to persist (no manual ordering in v1).
      if (!targetColumnId || targetColumnId === originalColumnId) {
        return;
      }

      const targetColumn = columns.find((column) => column.id === targetColumnId);
      if (!targetColumn) return;

      // `task.update` is optimistic and reverts on error internally. Re-sync the
      // board from the entities afterwards so a failed move snaps back.
      task
        .update(getColumnMovePatch(activeGroupBy, targetColumn))
        .catch(catchError)
        .finally(() => setItems(bucketTasksIntoColumns(columns, tasks, activeGroupBy.key)));
    };

    // Mirrors the list's `TaskCollection`: creating from a column header
    // pre-fills the grouped field so the new task lands in this column. The
    // "No X" buckets (value null) create with no grouping default.
    const handleCreate = (column: TaskColumnDescriptor) => {
      if (isViewOnly) {
        return authStore.setAuthorized(false);
      }

      openModal(CreateTaskModal, {
        title: 'Create task',
        project: taskLevel === 'space' ? undefined : project,
        defaultValues:
          column.value === null || column.value === undefined
            ? undefined
            : activeGroupBy.getDefaultValue(column.value),
      });
    };

    return (
      <div className={styles['container']}>
        <DragDropProvider
          onDragOver={(event) => {
            const { source } = event.operation;

            if (isDragDisabled || !source || source.type !== 'item') {
              return;
            }

            setItems((current) => move(current, event));
          }}
          onDragEnd={handleDragEnd}
        >
          {columns.map((column) => (
            <TaskColumn
              key={column.id}
              id={column.id}
              name={column.name}
              count={items[column.id]?.length ?? 0}
              icon={activeGroupBy.getIcon?.(column.value)}
              onCreate={() => handleCreate(column)}
            >
              {(items[column.id] ?? []).map((taskId, index) => (
                <TaskCard
                  key={taskId}
                  taskId={taskId}
                  index={index}
                  columnId={column.id}
                  dragDisabled={isDragDisabled}
                  onSelect={onSelectTask}
                />
              ))}
            </TaskColumn>
          ))}
        </DragDropProvider>
      </div>
    );
  },
);
